import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { ApiError } from '@crave/api-client';
import { Button, Card, Field, FormGrid } from '@crave/ui';
import { api } from '../api.ts';
import { ConfirmDelete } from '../components/ConfirmDelete.tsx';
import { HistoryLink } from '../components/HistoryLink.tsx';
import { Notice, Section, when } from '../components/ui.tsx';
import { STAFF_ME_KEY, useStaff, type StaffMe } from '../lib/staff.tsx';
import { Select } from './Inventory.tsx';

interface User {
  id: string;
  email: string;
  display_name: string;
  is_active: boolean;
  linked: boolean;
  last_login_at: string | null;
  roles: { role: string; location: string | null }[] | null;
  login: {
    exists: boolean;
    email_confirmed: boolean;
    last_sign_in_at: string | null;
    providers: string[];
  } | null;
  can_delete?: boolean;
  delete_blocked_reason?: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  manager: 'Manager',
  team_member: 'Kitchen & counter',
  kitchen: 'Kitchen',
  cashier: 'Front counter',
};
const ROLE_OPTIONS: [string, string][] = [
  ['owner', 'Owner'],
  ['manager', 'Manager'],
  ['team_member', 'Kitchen & counter'],
  ['kitchen', 'Kitchen'],
  ['cashier', 'Front counter'],
];
/** What each role opens, shown under the picker when adding someone. */
const ROLE_HINTS: Record<string, string> = {
  owner: 'Everything, in admin and the Kitchen app.',
  manager: 'Runs the location in admin, plus the kitchen queue and the POS.',
  team_member: 'Kitchen app: the kitchen queue and the POS.',
  kitchen: 'Kitchen app: the kitchen queue only, no POS.',
  cashier: 'Kitchen app: the POS only, no kitchen queue.',
};
const roleLabel = (code: string) => ROLE_LABELS[code] ?? code.replace(/_/g, ' ');

/** DELETE sends no body, so the optional reason travels as ?reason= (the API reads both). */
const withReason = (path: string, reason: string | undefined) =>
  reason ? `${path}?reason=${encodeURIComponent(reason)}` : path;
interface LoginOutcome {
  status: 'existing' | 'created' | 'password_set' | 'link';
  message: string;
  link?: string;
}
type LoginChoice = 'password' | 'link' | 'none';

const STAFF_HEAD = ['Name', 'Email', 'Role', 'Login', 'Last sign-in', ''];

/* Controls match the shared form fields (44px, 2px border). In a narrow space each staff member
   becomes a stacked, labelled block, and the login editor always opens as a full-width row. */
const STAFF_CSS = `
.st-control { height: var(--touch); padding: 0 10px; border: 2px solid var(--line); border-radius: var(--radius-sm); background: var(--paper); color: var(--ink); }
.st-link { flex: 1 1 240px; min-width: 0; padding: 0 12px; }
.st-login { border: 0; padding: 0; margin: 0; min-width: 0; }
.st-login > legend { float: left; width: 100%; padding: 0; margin: 0 0 6px; font-weight: 600; line-height: 1.3; }
.st-options { clear: both; display: grid; gap: 2px; }
.st-choice { display: grid; grid-template-columns: 18px minmax(0, 1fr); column-gap: 10px; align-items: start; padding: 5px 0; font-size: 14px; line-height: 20px; cursor: pointer; }
.st-choice > input { width: 18px; height: 18px; margin: 1px 0 0; }
@media (pointer: coarse) { .st-choice { min-height: var(--touch); align-content: center; } }
.st-password { display: grid; gap: 8px; max-width: 320px; margin-top: 10px; }
.st-list { container-type: inline-size; min-width: 0; }
.st-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.st-table th { text-align: left; padding: 8px 10px; border-bottom: 2px solid var(--line); color: var(--ink-soft); font-weight: 600; white-space: nowrap; }
.st-table td { padding: 8px 10px; border-bottom: 1px solid var(--line); vertical-align: top; }
.st-table td.st-empty { padding: 16px; color: var(--ink-soft); }
.st-table tr.st-open > td { border-bottom: 0; }
.st-val { min-height: var(--touch); display: flex; align-items: center; overflow-wrap: anywhere; }
.st-label { display: none; }
.st-role { min-width: 150px; width: 100%; }
.st-buttons { display: flex; gap: 8px; flex-wrap: wrap; justify-content: flex-end; }
.st-editor { display: grid; gap: 12px; padding: 4px 0 8px; }
.st-name .st-val { flex-direction: column; align-items: flex-start; justify-content: center; gap: 0; }
.st-table td.st-nowrap .st-val { white-space: nowrap; }
.st-table td.st-email .st-val { overflow-wrap: normal; }
.st-note { margin: 6px 0 0 auto; max-width: 300px; font-size: 13px; line-height: 1.35; color: var(--ink-soft); text-align: right; }
@container (max-width: 820px) {
  .st-table thead { display: none; }
  .st-table, .st-table tbody { display: block; }
  .st-table tr { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 150px), 1fr)); column-gap: 16px; row-gap: 8px; padding: 14px 0; border-bottom: 1px solid var(--line); }
  .st-table tr.st-open { border-bottom: 0; padding-bottom: 0; }
  .st-table td { display: block; padding: 0; border: 0; min-width: 0; }
  .st-table td.st-name, .st-table td.st-actions, .st-table td.st-empty, .st-table td.st-editor-cell { grid-column: 1 / -1; }
  .st-table td.st-name .st-val { min-height: 0; font-size: 16px; font-weight: 600; }
  .st-label { display: block; font-size: 12px; line-height: 1.3; color: var(--ink-soft); }
  .st-role { min-width: 0; }
  .st-buttons { justify-content: flex-start; }
  .st-note { margin-left: 0; max-width: none; text-align: left; }
}
`;

export function Staff() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['staff'],
    queryFn: async () =>
      (await api.get<{ items: User[]; locations: { id: string; name: string }[] }>('/admin/staff'))
        .data,
  });
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [form, setForm] = useState({ email: '', display_name: '', role: 'team_member' });
  const [loginChoice, setLoginChoice] = useState<LoginChoice>('password');
  const [tempPassword, setTempPassword] = useState('');
  const [shared, setShared] = useState<LoginOutcome | null>(null);
  const [managing, setManaging] = useState<string | null>(null);
  const [target, setTarget] = useState<User | null>(null);
  const { can } = useStaff();
  const canDelete = can('records.delete');
  // The admin shell loaded the signed-in staff member already; nobody deletes their own record.
  const meId = qc
    .getQueriesData<StaffMe>({ queryKey: [STAFF_ME_KEY] })
    .map(([, data]) => data?.user_id)
    .find(Boolean);
  const loginFor = (choice: LoginChoice, password: string) =>
    choice === 'password' ? { type: 'password', password } : { type: choice };
  const fail = (e: unknown) =>
    setMsg({ kind: 'error', text: e instanceof ApiError ? e.message : 'Something went wrong.' });
  const add = useMutation({
    mutationFn: async (v: Record<string, unknown>) =>
      (
        await api.post<{
          id: string;
          login: LoginOutcome | null;
          error?: { code: string; message: string };
        }>('/admin/staff', v)
      ).data,
    onSuccess: (r) => {
      if (r.error) setMsg({ kind: 'error', text: r.error.message });
      else {
        setMsg({ kind: 'ok', text: r.login?.message ?? 'Added.' });
        if (r.login?.link) setShared(r.login);
      }
      setForm({ email: '', display_name: '', role: 'team_member' });
      setTempPassword('');
      void qc.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: fail,
  });
  const manageLogin = useMutation({
    mutationFn: async (v: { id: string; body: Record<string, unknown> }) =>
      (await api.post<{ login: LoginOutcome }>(`/admin/staff/${v.id}/login`, v.body)).data,
    onSuccess: (r) => {
      setMsg({ kind: 'ok', text: r.login.message });
      if (r.login.link) setShared(r.login);
      setManaging(null);
      setTempPassword('');
      void qc.invalidateQueries({ queryKey: ['staff'] });
    },
    onError: fail,
  });
  const setRole = useMutation({
    mutationFn: (v: { id: string; role: string }) =>
      api.put(`/admin/staff/${v.id}/roles`, { roles: [{ role: v.role, location_id: null }] }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff'] }),
    onError: fail,
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; is_active: boolean }) =>
      api.patch(`/admin/staff/${v.id}`, { is_active: v.is_active }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['staff'] }),
    onError: fail,
  });
  const users = q.data?.items ?? [];
  return (
    <Section title="Staff">
      <style>{STAFF_CSS}</style>
      {msg && <Notice kind={msg.kind}>{msg.text}</Notice>}
      {shared?.link && (
        <Card>
          <strong>Set-password link</strong>
          <p style={{ margin: '6px 0', fontSize: 14 }}>
            Send it privately (text or email). It works once, and only for a limited time. Anyone
            with the link can set the password, so don't post it anywhere.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              readOnly
              value={shared.link}
              onFocus={(e) => e.target.select()}
              className="st-control st-link"
            />
            <Button
              variant="secondary"
              onClick={() =>
                void navigator.clipboard
                  ?.writeText(shared.link ?? '')
                  .then(() => setMsg({ kind: 'ok', text: 'Link copied.' }))
              }
            >
              Copy
            </Button>
            <Button variant="ghost" onClick={() => setShared(null)}>
              Done
            </Button>
          </div>
        </Card>
      )}
      <Card>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (loginChoice === 'password' && tempPassword.length < 8) {
              setMsg({ kind: 'error', text: 'Temporary passwords need at least 8 characters.' });
              return;
            }
            add.mutate({ ...form, location_id: null, login: loginFor(loginChoice, tempPassword) });
          }}
        >
          <FormGrid min={220}>
            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />
            <Field
              label="Name"
              value={form.display_name}
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              required
            />
            <Select
              label="Role"
              value={form.role}
              onChange={(v) => setForm({ ...form, role: v })}
              options={ROLE_OPTIONS}
              hint={ROLE_HINTS[form.role]}
            />
          </FormGrid>
          <LoginChooser
            choice={loginChoice}
            onChoice={setLoginChoice}
            password={tempPassword}
            onPassword={setTempPassword}
          />
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 16 }}>
            <Button type="submit" disabled={add.isPending}>
              {add.isPending ? 'Adding…' : 'Add staff member'}
            </Button>
          </div>
        </form>
        <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--ink-soft)' }}>
          Staff sign in at app.cravepizzafl.com (owners and managers) or kitchen.cravepizzafl.com.
          The same login also works on the ordering site.
        </p>
      </Card>
      <div className="st-list">
        <div style={{ overflowX: 'auto' }}>
          <table className="st-table">
            <thead>
              <tr>
                {STAFF_HEAD.map((h, i) => (
                  <th key={i}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan={STAFF_HEAD.length} className="st-empty">
                    Nothing here yet.
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <Fragment key={u.id}>
                  <tr className={managing === u.id ? 'st-open' : undefined}>
                    <td className="st-name">
                      <div className="st-val">
                        <span>{u.display_name}</span>
                        <HistoryLink entityType="app_users" entityId={u.id} name={u.display_name} />
                      </div>
                    </td>
                    <td className="st-email">
                      <span className="st-label">{STAFF_HEAD[1]}</span>
                      <div className="st-val">{u.email}</div>
                    </td>
                    <td>
                      <span className="st-label">{STAFF_HEAD[2]}</span>
                      <div className="st-val">
                        <select
                          value={u.roles?.[0]?.role ?? ''}
                          onChange={(e) => setRole.mutate({ id: u.id, role: e.target.value })}
                          className="st-control st-role"
                          aria-label={`Role for ${u.display_name}`}
                        >
                          {ROLE_OPTIONS.map(([r]) => (
                            <option key={r} value={r}>
                              {roleLabel(r)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="st-nowrap">
                      <span className="st-label">{STAFF_HEAD[3]}</span>
                      <div className="st-val">{loginLabel(u)}</div>
                    </td>
                    <td className="st-nowrap">
                      <span className="st-label">{STAFF_HEAD[4]}</span>
                      <div className="st-val">
                        {when(u.login?.last_sign_in_at ?? u.last_login_at, 'Never')}
                      </div>
                    </td>
                    <td className="st-actions">
                      <div className="st-buttons">
                        {managing !== u.id && (
                          <Button
                            variant="secondary"
                            disabled={!u.is_active}
                            onClick={() => {
                              setLoginChoice('password');
                              setTempPassword('');
                              setManaging(u.id);
                            }}
                          >
                            {u.login?.exists ? 'Reset login' : 'Create login'}
                          </Button>
                        )}
                        <Button
                          variant={u.is_active ? 'danger' : 'secondary'}
                          onClick={() => toggle.mutate({ id: u.id, is_active: !u.is_active })}
                        >
                          {u.is_active ? 'Deactivate' : 'Reactivate'}
                        </Button>
                        {canDelete && u.id !== meId && u.can_delete !== false && (
                          <Button
                            variant="danger"
                            aria-label={`Delete ${u.display_name}`}
                            onClick={() => {
                              setMsg(null);
                              setTarget(u);
                            }}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                      {canDelete &&
                        u.id !== meId &&
                        u.can_delete === false &&
                        u.delete_blocked_reason && (
                          <p className="st-note">{u.delete_blocked_reason}</p>
                        )}
                    </td>
                  </tr>
                  {managing === u.id && (
                    <tr>
                      <td colSpan={STAFF_HEAD.length} className="st-editor-cell">
                        <div className="st-editor">
                          <LoginChooser
                            choice={loginChoice === 'none' ? 'password' : loginChoice}
                            onChoice={setLoginChoice}
                            password={tempPassword}
                            onPassword={setTempPassword}
                            hideNone
                          />
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <Button
                              disabled={manageLogin.isPending}
                              onClick={() => {
                                const choice = loginChoice === 'none' ? 'password' : loginChoice;
                                if (choice === 'password' && tempPassword.length < 8) {
                                  setMsg({
                                    kind: 'error',
                                    text: 'Temporary passwords need at least 8 characters.',
                                  });
                                  return;
                                }
                                manageLogin.mutate({
                                  id: u.id,
                                  body: loginFor(choice, tempPassword),
                                });
                              }}
                            >
                              {manageLogin.isPending ? 'Saving…' : 'Save'}
                            </Button>
                            <Button variant="ghost" onClick={() => setManaging(null)}>
                              Close
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {target && (
        <ConfirmDelete
          open
          onClose={() => setTarget(null)}
          title={`Delete ${target.display_name}?`}
          description="They lose access to the Crave staff apps right away. Changes they made stay in the audit trail. If they have a Crave login, it is kept, because it also works as their customer account on the ordering site."
          consequences={(target.roles ?? []).map((r) =>
            r.location
              ? `Their ${roleLabel(r.role)} role at ${r.location}`
              : `Their ${roleLabel(r.role)} role`,
          )}
          confirmText={target.display_name}
          askReason
          onConfirm={async ({ reason }) => {
            const r = await api.delete<{ login_kept?: boolean }>(
              withReason(`/admin/staff/${target.id}`, reason),
            );
            setMsg({
              kind: 'ok',
              text: `Deleted ${target.display_name}.${
                r.data.login_kept ? ' Their Crave login still works on the ordering site.' : ''
              }`,
            });
            if (managing === target.id) setManaging(null);
            void qc.invalidateQueries({ queryKey: ['staff'] });
          }}
          alternative={
            target.is_active
              ? {
                  label: 'Deactivate',
                  description:
                    'You can deactivate them instead. They stay on this list and can no longer sign in to the staff apps.',
                  pendingLabel: 'Deactivating…',
                  onClick: async () => {
                    await api.patch(`/admin/staff/${target.id}`, { is_active: false });
                    setMsg({ kind: 'ok', text: `${target.display_name} deactivated.` });
                    void qc.invalidateQueries({ queryKey: ['staff'] });
                  },
                }
              : undefined
          }
        />
      )}
    </Section>
  );
}

function loginLabel(u: User): string {
  if (!u.login) return u.linked ? 'Linked' : 'Unknown';
  if (!u.login.exists) return 'No login yet';
  if (!u.login.email_confirmed) return 'Link not used yet';
  if (u.login.providers.includes('apple')) return 'Ready (Apple)';
  return 'Ready';
}

function LoginChooser({
  choice,
  onChoice,
  password,
  onPassword,
  hideNone,
}: {
  choice: LoginChoice;
  onChoice: (c: LoginChoice) => void;
  password: string;
  onPassword: (p: string) => void;
  hideNone?: boolean;
}) {
  const [show, setShow] = useState(false);
  const option = (value: LoginChoice, label: string) => (
    <label className="st-choice">
      <input
        type="radio"
        name={hideNone ? 'login-manage' : 'login-new'}
        checked={choice === value}
        onChange={() => onChoice(value)}
      />
      <span>{label}</span>
    </label>
  );
  return (
    <fieldset className="st-login">
      <legend>Login</legend>
      <div className="st-options">
        {option('password', 'Give them a temporary password')}
        {option('link', 'Create a link so they choose their own password')}
        {!hideNone && option('none', 'They already have a Crave login, or I will do this later')}
      </div>
      {choice === 'password' && (
        <div className="st-password">
          <Field
            label="Temporary password"
            type={show ? 'text' : 'password'}
            autoComplete="new-password"
            value={password}
            onChange={(e) => onPassword(e.target.value)}
            minLength={8}
            hint="At least 8 characters. Share it privately."
          />
          <label className="st-choice">
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} />
            <span>Show password</span>
          </label>
        </div>
      )}
    </fieldset>
  );
}
